import Lottie from 'lottie-react';
import { FC, useEffect, useState } from 'react';
import { CSSProperties } from 'styled-components';
import { useThemeContext } from '../../../global-state/themeContext';
import { Sizes, Typographies } from '../../../theme';
import { Typography, TypographyProps } from '../../atoms';
import { Button, ButtonProps } from '../button/Button';
import { AnimationWrapper } from './Animation.styles';

// Note: The value must match the animation file name
export enum Animations {
  SAD = 'sad',
  LOADING = 'loading',
  COOL = 'cool',
  HAPPY = 'happy',
  CONFUSED = 'confused',
  DEFAULT = 'default',
  NONE = 'none',
}

export type AnimationProps = {
  title: string;
  titleType?: Typographies;
  animation?: Animations;
  subtitle?: string;
  errorText?: string;
  button?: ButtonProps;
  size?: 'x-small' | Sizes;
  textConfig?: { title?: TypographyProps; subtitle?: TypographyProps };
  customWrapperStyle?: CSSProperties;
  onLoopComplete?: () => void;
};

export const Animation: FC<AnimationProps> = ({
  animation = Animations.DEFAULT,
  title,
  titleType = 'h2',
  subtitle,
  errorText,
  button,
  size = 'medium',
  textConfig,
  customWrapperStyle,
  onLoopComplete,
}) => {
  const [animationData, setAnimationData] = useState();
  const [hasLoaded, setHasLoaded] = useState(false);

  const { mode } = useThemeContext();

  useEffect(() => {
    if (animation === Animations.NONE) {
      setHasLoaded(true);
      return;
    }

    const animationURL = mode === 'dark' ? `${animation}_dark.json` : `${animation}.json`;

    import(`./animations/${animationURL}`).then((loadedAnimation) => {
      setAnimationData(loadedAnimation);
      setHasLoaded(true);
    });
  }, [animation, mode]);

  const getSize = () => {
    switch (size) {
      case 'x-small':
        return '100px';
      case 'small':
        return '150px';
      default:
      case 'medium':
        return '250px';
      case 'large':
        return '400px';
    }
  };

  if (!hasLoaded) return null;

  return (
    <AnimationWrapper style={customWrapperStyle} data-testid="animation">
      {animation !== Animations.NONE && (
        <Lottie
          animationData={animationData}
          autoPlay
          loop
          style={{
            height: getSize(),
            width: getSize(),
          }}
          onLoopComplete={onLoopComplete}
        />
      )}

      <Typography type={titleType} textAlign="center" {...textConfig?.title}>
        {title}
      </Typography>
      {subtitle && (
        <Typography type="body3" textAlign="center" margin={[0, 0, 4, 0]} {...textConfig?.subtitle}>
          {subtitle}
        </Typography>
      )}
      {errorText && (
        <Typography type="button" textAlign="center" margin={[0, 0, 4, 0]}>
          {errorText}
        </Typography>
      )}
      {button && <Button {...button} />}
    </AnimationWrapper>
  );
};
